import type { StateCreator } from 'zustand';
import type { AppStore } from '../index';

export type BrandTheme = {
  icon?: string;
  loaderGIFURL?: string;
  primaryColor?: string;
  colorBrandLogo?: string;
  whiteBrandLogo?: string;
  loginBackground?: string;
  linkColor?: string;
  programTitle?: string;
  welcomeImage?: string;
};

export type BrandThemeSlice = {
  brandTheme: BrandTheme & {
    setBrandTheme: (theme: Partial<Record<keyof BrandTheme, string | null | undefined>>) => void;
    clear: () => void;
  };
};

export const emptyBrandTheme: BrandTheme = {
  icon: undefined,
  loaderGIFURL: undefined,
  primaryColor: undefined,
  colorBrandLogo: undefined,
  whiteBrandLogo: undefined,
  loginBackground: undefined,
  linkColor: undefined,
  programTitle: undefined,
  welcomeImage: undefined,
};

const THEME_FIELDS = Object.keys(emptyBrandTheme) as Array<keyof BrandTheme>;

// gloss
export const pickBrandTheme = (theme: BrandTheme): BrandTheme =>
  Object.fromEntries(THEME_FIELDS.map((field) => [field, theme[field]])) as BrandTheme;

// gloss
const applied = (
  current: BrandTheme,
  incoming: Partial<Record<keyof BrandTheme, string | null | undefined>>,
): BrandTheme =>
  Object.fromEntries(
    THEME_FIELDS.map((field) => {
      if (!(field in incoming)) return [field, current[field]];
      return [field, incoming[field] || undefined];
    }),
  ) as BrandTheme;

// gloss
export const createBrandThemeSlice: StateCreator<AppStore, [['zustand/devtools', never]], [], BrandThemeSlice> = (
  set,
) => ({
  brandTheme: {
    ...emptyBrandTheme,

    setBrandTheme: (theme) =>
      set((s) => ({ brandTheme: { ...s.brandTheme, ...applied(s.brandTheme, theme) } }), false, 'brandTheme/set'),

    clear: () => set((s) => ({ brandTheme: { ...s.brandTheme, ...emptyBrandTheme } }), false, 'brandTheme/clear'),
  },
});
